import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { RecallTopicBadge } from '@/components/badges/RecallTopicBadge';
import { BrandChip } from '@/components/BrandChip';
import { type DataTableColumn, DataTable } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    allBrandsState,
    allEquipmentTypesState,
    equipmentTypeFamiliesState,
    RECALLS_DEFAULT_FILTER,
    recallsState,
    type RecallsFilter,
    recallTopicsState,
} from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { formatDate } from '@/util/dates';
import { followLiveBrand } from '@rock/brands';
import { useLoadMore } from '@rock/db-admin';
import type { Brand, BrandId, Recall, RecallTopicId } from '@rock/types';
import { recallKey } from '@rock/types';
import { familyOf } from '@rock/util';
import { type FC, useEffect, useMemo } from 'react';
import { RECALL_STATUS_META } from './recallStatus';
import { PageUnavailableBadge } from './recallTags';

/**
 * The paged recall table under one filter. Every recall list shares the one `recallsState` atom, so the
 * list pushes its filter in whenever the two differ.
 */
export const RecallsTable: FC<{ filter: RecallsFilter; emptyText: string; hideTypes?: boolean }> = ({
    filter,
    emptyText,
    hideTypes,
}) => {
    const {
        data: recalls,
        isLoading,
        hasError,
        hasMore,
        loadMore,
        filter: liveFilter,
        setFilter,
    } = useLoadMore(recallsState);
    const synced = sameFilter(liveFilter, filter);
    useEffect(() => {
        if (!synced) setFilter(filter);
    }, [synced, filter, setFilter]);

    return (
        <>
            {!synced || recalls.length === 0 ? (
                <div className="section-description">
                    {!synced || isLoading ? 'Loading recalls…' : hasError ? 'Couldn’t load recalls.' : emptyText}
                </div>
            ) : (
                <RecallRows recalls={recalls} hideTypes={hideTypes} />
            )}
            {synced && hasMore && (
                <div className="mt-4 flex justify-center">
                    <Button variant="outline" onClick={() => loadMore()}>
                        Load More
                    </Button>
                </div>
            )}
        </>
    );
};

/** Recall rows, each linking to its details. For a list the caller already holds. */
export const RecallRows: FC<{ recalls: Recall[]; hideTypes?: boolean }> = ({ recalls, hideTypes }) => {
    const { typeLabels, topicTitles, brandsById } = useRecallCatalog();
    const columns = useMemo(
        () =>
            recallColumns(typeLabels, topicTitles, brandsById).filter((column) => !hideTypes || column.key !== 'types'),
        [typeLabels, topicTitles, brandsById, hideTypes],
    );
    return (
        <DataTable
            columns={columns}
            rows={recalls}
            rowKey={(recall) => recall.id}
            rowHref={(recall) => `/products/recalls/${encodeURIComponent(recall.id)}`}
        />
    );
};

function sameFilter(a: RecallsFilter, b: RecallsFilter): boolean {
    return (Object.keys(RECALLS_DEFAULT_FILTER) as (keyof RecallsFilter)[])
        .concat('untypedKind')
        .every((key) => a[key] === b[key]);
}

/**
 * Type labels, topic titles and catalog brands for the ids a recall carries. A type's label names its family
 * too: a recall listing it also asks owners of the family's other types.
 */
function useRecallCatalog(): {
    typeLabels: Map<string, string>;
    topicTitles: Map<RecallTopicId, string>;
    brandsById: Map<BrandId, Brand>;
} {
    const brands = useAsyncAtomValue(allBrandsState, []);
    const topics = useAsyncAtomValue(recallTopicsState, []);
    const topicTitles = useMemo(
        () => new Map<RecallTopicId, string>(topics.map((topic) => [topic.id, topic.title])),
        [topics],
    );
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const typeLabels = useMemo(
        () =>
            new Map(
                types.map((type) => {
                    const title = type.shortTitle ?? type.title;
                    const family = familyOf(type.id, families);
                    return [type.id, family ? `${title} · ${family.name} family` : title];
                }),
            ),
        [types, families],
    );
    const brandsById = useMemo(() => new Map<BrandId, Brand>(brands.map((brand) => [brand.id, brand])), [brands]);
    return { typeLabels, topicTitles, brandsById };
}

function recallColumns(
    typeLabels: Map<string, string>,
    topicTitles: Map<RecallTopicId, string>,
    brandsById: Map<BrandId, Brand>,
): DataTableColumn<Recall>[] {
    return [
        {
            key: 'published',
            header: 'Published',
            className: 'w-[110px]',
            cell: (recall) => formatDate(recall.publishedAt),
        },
        {
            key: 'source',
            header: 'Recall',
            className: 'w-[110px] font-mono text-xs',
            cell: (recall) => recallKey(recall.source, recall.sourceId),
        },
        {
            key: 'title',
            header: 'Title',
            className: 'min-w-[240px] max-w-md',
            cellClassName: 'whitespace-normal',
            cell: (recall) => (
                <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{recall.title}</span>
                    {recall.classification?.headline && (
                        <span className="text-xs text-muted-foreground">{recall.classification.headline}</span>
                    )}
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            className: 'w-[120px]',
            cell: (recall) => {
                const meta = RECALL_STATUS_META[recall.status];
                return (
                    <span className="flex flex-wrap items-center gap-1">
                        <Badge variant={meta.variant} className={meta.className}>
                            {meta.label}
                        </Badge>
                        {recall.draftRules && <Badge variant="outline">re-review</Badge>}
                        {recall.pageUnavailable && <PageUnavailableBadge pageUnavailable={recall.pageUnavailable} />}
                    </span>
                );
            },
        },
        {
            key: 'types',
            header: 'Types',
            className: 'min-w-[160px]',
            cell: (recall) => {
                const classification = recall.classification;
                if (!classification) return <span className="text-muted-foreground">—</span>;
                if (classification.typeIds.length === 0) {
                    return <span className="text-muted-foreground">untyped · {classification.productKind}</span>;
                }
                return (
                    <span className="flex flex-wrap gap-1">
                        {classification.typeIds.map((id) => (
                            <EquipmentTypeBadge key={id} typeId={id} title={typeLabels.get(id)} />
                        ))}
                    </span>
                );
            },
        },
        {
            key: 'topics',
            header: 'Topics',
            className: 'min-w-[140px]',
            cell: (recall) =>
                recall.classification?.topicIds.length ? (
                    <span className="flex flex-wrap gap-1">
                        {recall.classification.topicIds.map((id) => (
                            <RecallTopicBadge key={id} topicId={id} title={topicTitles.get(id)} />
                        ))}
                    </span>
                ) : (
                    <span className="text-muted-foreground">—</span>
                ),
        },
        {
            key: 'brands',
            header: 'Brands',
            className: 'min-w-[160px]',
            cell: (recall) =>
                recall.brandIds.length ? (
                    <span className="flex flex-wrap gap-1">
                        {recall.brandIds.map((id) => {
                            const brand = followLiveBrand(id, (next) => brandsById.get(next)) ?? brandsById.get(id);
                            return brand ? (
                                <BrandChip key={id} brand={brand} />
                            ) : (
                                <span key={id} className="font-mono text-xs text-muted-foreground">
                                    {id}
                                </span>
                            );
                        })}
                    </span>
                ) : (
                    <span className="text-muted-foreground">—</span>
                ),
        },
        {
            key: 'hits',
            header: 'Models / Units',
            className: 'w-[120px] text-right tabular-nums',
            cell: (recall) => (recall.dryRun ? `${recall.dryRun.modelIds.length} / ${recall.dryRun.unitCount}` : '—'),
        },
    ];
}
